import { AGENCY_COLUMNS } from '@/types/distributors/networkColumnDef';
import { useForm, useWatch } from 'react-hook-form';
import { LabelValue } from '@/types/global';
import StyledSelect from '../styled/StyledSelect';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { RowSelectionState } from '@tanstack/react-table';
import { useFindAgenciesByNetwork } from '@/hooks/useDistributors';
import useTableControls from '@/hooks/useTableControls';
import { PAGE_SIZE } from '@/constants/pageSize';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import StyledInput from '../styled/StyledInput';
import SearchIcon from '@/assets/icons/search.svg?react';
import Loader from '../ui/Loader';
import { useStyledTable } from '@/hooks/useStyledTable';
import DataTable from '../ui/DataTable';
import DistributorInfoModal from './modals/DistributorInfoModal';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface AgenciesSubListProps {
  networkId: string;
  setSelectedAgencies: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  shouldSelectAll: boolean;
  recentOnly?: boolean;
}

const AgenciesSubList = ({
  networkId,
  setSelectedAgencies,
  shouldSelectAll = false,
  recentOnly = false,
}: AgenciesSubListProps) => {
  'use no memo';

  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<{
    agency: string;
    country?: LabelValue<string>;
    city?: LabelValue<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      agency: '',
      country: undefined,
      city: undefined,
    },
  });

  const searchName = useWatch({ control, name: 'agency' });
  const selectedCountry = useWatch({ control, name: 'country' });
  const selectedCity = useWatch({ control, name: 'city' });

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const filters = useMemo(
    () => ({
      name: searchName ?? '',
      country: selectedCountry?.value ?? '',
      city: selectedCity?.value ?? '',
    }),
    [searchName, selectedCity?.value, selectedCountry?.value],
  );

  const {
    data: agenciesResponse,
    isSuccess,
    isLoading,
    isRefetching,
  } = useFindAgenciesByNetwork(
    networkId,
    {
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortOrder,
      filters,
      recentOnly,
    },
    (response) => ({
      ...response,
      countries: response.countries.map(({ country }) => ({
        label: country,
        value: country,
      })),
      cities: response.cities.map(({ city }) => ({
        label: city,
        value: city,
      })),
    }),
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [distributorToViewId, setDistributorToViewId] = useState<string | null>(
    null,
  );

  const table = useStyledTable({
    columns: getTranslatedConst(AGENCY_COLUMNS),
    data: agenciesResponse?.agencies ?? [],
    getRowId: (row) => row.id,
    enablePagination: false,
    rowSelection,
    setSelection: setRowSelection,
    sorting,
    setSorting,
    onViewRow: (row) => {
      setDistributorToViewId(row.original.id);
    },
  });

  const prevShouldSelectAllRef = useRef(false);
  const prevIsSuccessRef = useRef(false);
  const prevIsRefetchingRef = useRef(false);

  useEffect(() => {
    if (
      (shouldSelectAll !== prevShouldSelectAllRef.current ||
        isSuccess !== prevIsSuccessRef.current ||
        isRefetching !== prevIsRefetchingRef.current) &&
      isSuccess
    ) {
      if (shouldSelectAll) {
        table.toggleAllRowsSelected(true);
      } else {
        table.toggleAllRowsSelected(false);
        startTransition(() => {
          setRowSelection({});
        });
      }
      prevShouldSelectAllRef.current = shouldSelectAll;
      prevIsSuccessRef.current = isSuccess;
      prevIsRefetchingRef.current = isRefetching;
    }
  }, [shouldSelectAll, isSuccess, isRefetching, table]);

  useEffect(() => {
    setSelectedAgencies((prevSelection) => ({
      ...prevSelection,
      [networkId]: Object.keys(rowSelection),
    }));
  }, [networkId, rowSelection, setSelectedAgencies]);

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <>
        <div className="flex flex-col lg:flex-row">
          <div className="flex flex-col gap-4 p-6">
            <span className="text-xs font-normal text-gray-600">
              {t('distributors.filterBy')}
            </span>
            <StyledInput
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              name="agency"
              placeholder={t('distributors.quickSearchPlaceholder')}
              icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
            />
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={agenciesResponse.countries}
              name="country"
              isClearable
              placeholder={t('distributors.columns.country')}
            />
            <StyledSelect
              className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
              control={control}
              options={agenciesResponse.cities}
              name="city"
              isClearable
              placeholder={t('distributors.columns.city')}
            />
            <StyledNextPreviousButtons
              page={page}
              pageSize={PAGE_SIZE}
              setPage={setPage}
              dataLength={agenciesResponse.total}
              label="agence"
              minimized
            />
          </div>
          <div className="w-full max-h-[750px] overflow-auto">
            <DataTable table={table} sorting={sorting} />
          </div>
        </div>
        {distributorToViewId && (
          <DistributorInfoModal
            distributorId={distributorToViewId}
            closeModal={() => setDistributorToViewId(null)}
          />
        )}
      </>
    )
  );
};

export default AgenciesSubList;
